import { MouseEvent } from 'react';
import clsx from 'clsx';
import { IAnyType } from 'common/types/common';
import { IProjectItem } from 'common/types/projects';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

interface Props {
  onImageClick: (src: string) => (e: MouseEvent<HTMLButtonElement>) => void;
  project: IProjectItem;
}

export const ProjectContent: React.FC<Props> = ({ onImageClick, project }) => {
  const { t } = useTranslation('projectItemsDetails');
  const projectSections = Array.isArray(project.sections) ? project.sections : [];
  const testimonialAuthorImageSrc = project.testimonial?.author?.image?.src;

  const renderSectionContent =
    (dark: boolean, indexSection: number) => (item: IAnyType, indexItem: number) => {
      if (item.type === 'paragraph') {
        return (
          <div
            key={`section-item-${indexSection}-${indexItem}`}
            className={clsx('mb-6', item.className, {
              'text-black': !dark,
              'text-white': dark,
            })}>
            {!!item.title && (
              <h3
                className="mb-2 text-sm font-bold uppercase"
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
            )}
            <p className={item.className} dangerouslySetInnerHTML={{ __html: item.value }} />
          </div>
        );
      } else if (item.type === 'list') {
        return (
          <div
            key={`section-item-${indexSection}-${indexItem}`}
            className={clsx('mb-6', item.className, {
              'text-black': !dark,
              'text-white': dark,
            })}>
            {!!item.title && (
              <h3
                className="mb-2 text-sm font-bold uppercase"
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
            )}
            <ul className={clsx({ 'text-black': !dark, 'text-white': dark })}>
              {item.value.map((label: IAnyType, index: number) => (
                <li
                  key={`section-item-${indexSection}-${indexItem}-list-${index}`}
                  className="mt-3 flex items-start">
                  <div className="mr-3 mt-4 h-[3px] w-5 bg-primary" />
                  <div dangerouslySetInnerHTML={{ __html: label }} />
                </li>
              ))}
            </ul>
          </div>
        );
      } else if (item.type === 'grid') {
        return (
          <div
            key={`section-item-${indexSection}-${indexItem}`}
            className={clsx('mt-6 grid gap-x-6 gap-y-10 lg:mt-12 lg:gap-x-12', {
              'grid-cols-2 md:grid-cols-3': item.value.length > 3,
              'grid-cols-2': item.value.length <= 3,
            })}>
            {item.value.map((innerItem: IAnyType, indexGrid: number) => (
              <div key={`grid-${indexSection}-${indexGrid}`}>
                <h3 className="mb-1 text-sm font-bold uppercase text-primary">{innerItem.title}</h3>
                <p>{innerItem.value}</p>
              </div>
            ))}
          </div>
        );
      } else if (item.type === 'image') {
        return (
          <div
            key={`section-item-${indexSection}-${indexItem}`}
            className={clsx(`relative w-full translate-y-[30px] overflow-hidden`, item.className, {
              'drop-shadow-xl': !!item.hasShadow,
            })}>
            {!!item.value.src && (
              <button onClick={onImageClick(item.value.src)}>
                <Image
                  className={clsx('h-full w-full object-cover', item.value.className)}
                  alt={item.value.alt}
                  src={item.value.src}
                  height={item.value.height}
                  width={item.value.width / 2}
                />
              </button>
            )}
          </div>
        );
      } else if (item.type === 'servicesAndTechnologies') {
        return (
          <div
            key={`section-item-${indexSection}-${indexItem}`}
            className="mb-12 flex flex-col lg:flex-row">
            <div className="flex flex-1 flex-col">
              <h3
                className={clsx('max-w-[760px] mb-2 text-sm font-bold uppercase', {
                  'text-black': !dark,
                  'text-white': dark,
                })}>
                {t('services')}
              </h3>
              <ul className={clsx({ 'text-black': !dark, 'text-white': dark })}>
                {item.services.map((service: IAnyType, index: number) => (
                  <li key={`services-${index}`} className="flex items-start gap-1 py-1">
                    {service.label} <span className="ml-2 mt-4 h-[2px] w-5 bg-primary" />
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 flex flex-1 flex-col lg:mt-0">
              <h3
                className={clsx('max-w-[760px] mb-2  text-sm font-bold uppercase', {
                  'text-black': !dark,
                  'text-white': dark,
                })}>
                {t('technologies')}
              </h3>
              <ul className={clsx({ 'text-black': !dark, 'text-white': dark })}>
                {item.technologies.map((technology: IAnyType, index: number) => (
                  <li key={`technologies-${index}`} className="flex items-start gap-1 py-1">
                    <span className="mr-2 mt-4 h-[2px] w-3 bg-primary" /> {technology.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      }
    };

  const renderSections = () => {
    return projectSections.map((section: IAnyType, index: number) => {
      const bgClassName = section.gradient ? 'bg-gradient' : 'bg-bgGrayLight';
      if (section.type === 'sectionBox') {
        return (
          <section
            key={`section-${index}`}
            className={clsx('px-4', section.className, bgClassName)}>
            <div className="container px-0">
              <div
                className={clsx(
                  'work-heading px-8 py-12 md:mx-4 md:px-6 lg:px-20 drop-shadow-sm',
                  section.gradient ? 'border border-white/10 bg-white/5' : 'bg-white',
                )}>
                {section.value.map(renderSectionContent(section.gradient, index))}
              </div>
            </div>
          </section>
        );
      }
      if (section.type === 'sectionImage') {
        return (
          <section key={`section-${index}`} className={clsx(section.className, bgClassName)}>
            <div
              className={clsx(
                'container',
                `grid justify-center grid-cols-${section.value.length} gap-x-4`,
              )}>
              {section.value.map(renderSectionContent(section.gradient, index))}
            </div>
          </section>
        );
      }
      return (
        <section
          key={`section-${index}`}
          className={clsx(
            bgClassName,
            section.className,
            index + 1 === projectSections.length && 'pb-16 lg:pb-32',
          )}>
          <div className="container">
            <div className="grid gap-5 md:gap-10 lg:grid-cols-2">
              <h2
                className={clsx('heading-work-3 mb-6 font-bold', {
                  'text-black': !section.gradient,
                  'text-white': section.gradient,
                })}
                dangerouslySetInnerHTML={{ __html: section.title }}
              />

              <div>{section.value.map(renderSectionContent(section.gradient, index))}</div>
            </div>
          </div>
        </section>
      );
    });
  };

  return (
    <div className="text-lg">
      {renderSections()}

      {project.testimonial?.visible && (
        <section className="bg-white py-20 lg:py-32">
          <div className="container z-50">
            <div className="grid gap-12 lg:grid-cols-2">
              <div className="flex justify-end">
                <div
                  className="josefin max-w-[600px] text-center font-light italic text-gray-700 lg:text-right"
                  dangerouslySetInnerHTML={{ __html: '“' + project.testimonial.text + '“' }}
                />
              </div>

              <div className="flex items-center justify-center text-primary md:items-end md:justify-start">
                <div className="h-40 w-40 overflow-hidden rounded-[70px]">
                  {!!testimonialAuthorImageSrc && (
                    <Image
                      className="h-full w-full object-cover object-center"
                      alt={project.testimonial.author.name + '_' + project.testimonial.author.role}
                      src={testimonialAuthorImageSrc}
                      height={project.testimonial.author.image.height}
                      width={project.testimonial.author.image.width}
                    />
                  )}
                </div>
                <div className="ml-4 pb-4 lg:ml-2">
                  <div className="italic text-2xl font-semibold text-black">
                    {project.testimonial.author.name}
                  </div>
                  <div className="mt-1 italic text-black">{project.testimonial.author.role}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
